import React from 'react';

import { getIsFileExtractable } from '@aline/common';
import { gql } from '@apollo/client';
import { CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineButton } from 'src/components';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

export enum OCRStatus {
  NotRun,
  Running,
  Finished,
  Failed,
  NotOCRAble,
}

void gql`
  query GetOCRDocument($documentId: UUID!) {
    document(documentId: $documentId) {
      __typename
      id
      variant
      latestVersion {
        ... on FileVersionInterface {
          __typename
          id
          vectorsFailedAt
          vectorsSavedAt
          vectorsStartedAt
        }
        ... on StaticFileVersion {
          __typename
          id
          fileExtractionFailedAt
          fileExtractionFinishedAt
          fileExtractionStartedAt
          fileLocation {
            __typename
            id
            mimetype
          }
        }
      }
    }
  }
`;

export function useOCRStatus(): { ocrStatus: OCRStatus } {
  const { documentId } = useDocumentParams();
  const { data } = Apollo.useGetOcrDocumentQuery({ variables: { documentId } });
  if (!data?.document || data.document.latestVersion.__typename === 'DocumentVersion')
    return { ocrStatus: OCRStatus.Finished };

  return { ocrStatus: getOCRStatusFromFileVersion(data.document.latestVersion) };
}

const useStyles = makeStyles()(theme => ({
  ocr: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
  },
  panelContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    padding: theme.spacing(2),
  },
}));

export function OCRStatusOrChildren({
  children,
  documentId,
}: {
  children: React.ReactNode;
  documentId: string;
}): JSX.Element {
  const { classes } = useStyles();
  const { ocrStatus } = useOCRStatus();
  const [startOCR, { loading: startingOCR }] = Apollo.useStartOcrForDocumentsMutation();

  switch (ocrStatus) {
    case OCRStatus.NotOCRAble: {
      return (
        <div className={clsx(classes.panelContainer, classes.ocr)}>
          <WarningIcon />
          <Typography align="center">Cannot OCR file. Aline AI unavailable.</Typography>
        </div>
      );
    }
    case OCRStatus.NotRun: {
      return (
        <div className={clsx(classes.panelContainer, classes.ocr)}>
          <WarningIcon />
          <Typography align="center">OCR must be run prior to running Aline AI.</Typography>
          <AlineButton
            color="primary"
            loading={startingOCR}
            variant="contained"
            onClick={async () => {
              await startOCR({ variables: { documentIds: [documentId] } });
            }}
          >
            Run OCR
          </AlineButton>
        </div>
      );
    }
    case OCRStatus.Running: {
      return (
        <div className={clsx(classes.panelContainer, classes.ocr)}>
          <CircularProgress size={28} />
          <Typography align="center">
            OCR running and must successfully finish prior to running Aline AI.
          </Typography>
        </div>
      );
    }
    case OCRStatus.Failed: {
      return (
        <div className={clsx(classes.panelContainer, classes.ocr)}>
          <ErrorIcon />
          <Typography align="center">
            OCR failed and must successfully finish prior to running Aline AI.
          </Typography>
          <AlineButton
            color="primary"
            loading={startingOCR}
            variant="contained"
            onClick={async () => {
              await startOCR({ variables: { documentIds: [documentId] } });
            }}
          >
            Retry OCR
          </AlineButton>
        </div>
      );
    }
    case OCRStatus.Finished: {
      return <>{children}</>;
    }
  }
}

export interface StaticVersionWithExtractionInfo extends Pick<
  Apollo.StaticFileVersion,
  | 'fileExtractionFailedAt'
  | 'fileExtractionFinishedAt'
  | 'fileExtractionStartedAt'
  | 'id'
  | '__typename'
> {
  fileLocation: Pick<Apollo.DocFile, 'id' | 'mimetype' | '__typename'>;
}
// Aline Document does not have extraction info, so we just use the id and typename.
export type DocumentVersionWithExtractionInfo = Pick<Apollo.DocumentVersion, 'id' | '__typename'>;
export type FileVersionWithExtractionInfo =
  | DocumentVersionWithExtractionInfo
  | StaticVersionWithExtractionInfo;

export function getOCRStatusFromFileExtractionInfo(
  fileVersion: FileVersionWithExtractionInfo
): OCRStatus {
  switch (fileVersion.__typename) {
    case 'DocumentVersion':
      return OCRStatus.Finished;
    case 'StaticFileVersion': {
      const isOCRAble = getIsFileExtractable(fileVersion.fileLocation.mimetype);
      if (!isOCRAble) return OCRStatus.NotOCRAble;

      const isOCRStarted = Boolean(fileVersion.fileExtractionStartedAt);
      if (!isOCRStarted) return OCRStatus.NotRun;

      const isOCRFailed = Boolean(fileVersion.fileExtractionFailedAt);
      if (isOCRFailed) return OCRStatus.Failed;

      const isOCRFinished = Boolean(fileVersion.fileExtractionFinishedAt);
      return isOCRFinished ? OCRStatus.Finished : OCRStatus.Running;
    }
  }
}

export type DocumentVersionWithProcessingInfo = Pick<
  Apollo.DocumentVersion,
  'vectorsFailedAt' | 'vectorsSavedAt' | 'vectorsStartedAt' | 'id' | '__typename'
>;
export interface StaticVersionWithProcessingInfo extends Pick<
  Apollo.StaticFileVersion,
  | 'fileExtractionFailedAt'
  | 'fileExtractionFinishedAt'
  | 'fileExtractionStartedAt'
  | 'vectorsFailedAt'
  | 'vectorsSavedAt'
  | 'vectorsStartedAt'
  | 'id'
  | '__typename'
> {
  fileLocation: Pick<Apollo.DocFile, 'id' | 'mimetype' | '__typename'>;
}
export type FileVersionWithProcessingInfo =
  | DocumentVersionWithProcessingInfo
  | StaticVersionWithProcessingInfo;

export function getOCRStatusFromFileVersion(fileVersion: FileVersionWithProcessingInfo): OCRStatus {
  const ocrStatus = getOCRStatusFromFileExtractionInfo(fileVersion);

  // For extractable files (PDFs), extraction must complete before checking vectors.
  // NotOCRAble means extraction not needed (e.g., DOCX) - skip to vector check.
  if (ocrStatus !== OCRStatus.Finished && ocrStatus !== OCRStatus.NotOCRAble) {
    return ocrStatus;
  }

  switch (fileVersion.__typename) {
    case 'DocumentVersion':
    case 'StaticFileVersion': {
      const isVectorStarted = Boolean(fileVersion.vectorsStartedAt);
      if (!isVectorStarted) return OCRStatus.NotRun;

      const isVectorFailed = Boolean(fileVersion.vectorsFailedAt);
      if (isVectorFailed) return OCRStatus.Failed;

      const isVectorFinished = Boolean(fileVersion.vectorsSavedAt);
      return isVectorFinished ? OCRStatus.Finished : OCRStatus.Running;
    }
  }
}

/**
 * Check if an OCR status indicates the document is still processing.
 */
export function isOcrProcessing(status: OCRStatus | null): boolean {
  return status === OCRStatus.Running || status === OCRStatus.NotRun;
}
